import AnalyseDropDown from '../../shop/analyse/AnalyseDropDown';
import { BulkActionButtons } from '../search/BulkActionButtons';
import React, { FC, useCallback, useLayoutEffect, useMemo, useState } from 'react';

import './BundleHeader.scss';
import TotalAmountBadges from '../common/TotalAmountBadges';
import {
    AnalyseResponseOut,
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse,
    BundleOut
} from '../../store/api';
import { PriceTier } from '../../../models/PriceTier';
import { SelectionList } from '../../../models/SelectionList';
import { useBundleDetailTotals, UseFetchBundleTotalsQuery } from '../../api/bundles/useBundleDetailTotals';
import BundleDiscountLine from './BundleDiscountLine';
import { LoanFormat, mapLoanFormat } from '../../../models/LoanFormat';
import { fetchBundleAnalyse } from '../../api/bundles/fetchAnalyse';
import AnalyseTable from '../../shop/analyse/AnalyseTable';
import { useTranslation } from 'react-i18next';
import { SubscriptionPeriodType } from '../../../models/Article';

const buildTotals = (bundle: BundleOut | undefined, bundleTotalsQuery: UseFetchBundleTotalsQuery) => {
    const totals = {
        ebooksAmount: 0,
        ebooksValue: 0,
        eaudiobooksAmount: 0,
        eaudiobooksValue: 0,
        epressAmount: 0,
        epressValue: 0,
        currency: 'AUD',
        isLoading: false
    };

    if (bundle?.loanFormat) {
        const bundleTotalsValue = bundleTotalsQuery.data?.netValue;
        const amount = bundle.productAmount ?? 0;
        const value = bundleTotalsValue ?? bundle.customerPrice ?? 0;

        totals[`${mapLoanFormat(bundle.loanFormat)!.toLowerCase()}Amount`] = amount;
        totals[`${mapLoanFormat(bundle.loanFormat)!.toLowerCase()}Value`] = value;
        totals.currency = bundle.currency ?? '';
        totals.isLoading = bundleTotalsQuery.status === 'loading';
    }

    return totals;
};

type Props = {
    readonly bundle: (BundleOut & { basePrice?: number }) | undefined;
    readonly filtered: boolean;
    readonly onAddBundleToList: (
        list: SelectionList,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onAddBundleToCart: (
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onCreateNewListClicked?: ((newListName: string) => void) | undefined;
    readonly selectionLists: ReadonlyArray<SelectionList>;
    readonly hasSingleUsageCondition: boolean;
};

export const BundleHeader: FC<Props> = ({
    bundle,
    filtered,
    onAddBundleToList,
    onAddBundleToCart,
    onCreateNewListClicked,
    selectionLists,
    hasSingleUsageCondition
}) => {
    const { t } = useTranslation();
    const [priceTier, setPriceTier] = useState<PriceTier>({ lendingModel: 'OCOU', level: 'MIN' });
    const bundleTotalsQuery = useBundleDetailTotals(bundle?.bundleId, priceTier);
    const totals = useMemo(() => buildTotals(bundle, bundleTotalsQuery), [bundle, bundleTotalsQuery]);

    const handleBulkAddToList = useCallback(
        (
            list: SelectionList,
            priceTier: PriceTier,
            subscriptionPeriodType: SubscriptionPeriodType,
            performChecks: boolean
        ) => onAddBundleToList(list, priceTier, subscriptionPeriodType, performChecks),
        [onAddBundleToList]
    );

    const [analyse, setAnalyse] = useState<AnalyseResponseOut | undefined>(undefined);

    const [isAnalyseOpen, setIsAnalyseOpen] = useState(false);
    const [isAnalyseLoading, setIsAnalyseLoading] = useState(true);

    const onAnalyseClicked = useCallback(() => setIsAnalyseOpen(prevState => !prevState), []);

    useLayoutEffect(() => {
        const fetchAnalyse = async () => {
            if (bundle?.bundleId && isAnalyseOpen) {
                try {
                    setIsAnalyseLoading(true);
                    const res = await fetchBundleAnalyse(bundle.bundleId);
                    setAnalyse(res);
                } catch (e) {
                    setAnalyse(undefined);
                } finally {
                    setIsAnalyseLoading(false);
                }
            }
        };

        fetchAnalyse().then();
    }, [isAnalyseOpen, bundle?.bundleId]);

    const handleBulkAddToCart = useCallback(
        (priceTier: PriceTier, subscriptionPeriodType: SubscriptionPeriodType, performChecks: boolean) =>
            onAddBundleToCart(priceTier, subscriptionPeriodType, performChecks),
        [onAddBundleToCart]
    );

    const listLabel = filtered ? t('bundles.add.selectedProductsToList') : t('bundles.add.completeCollectionToList');
    const cartLabel = filtered ? t('bundles.add.selectedProductsToCart') : t('bundles.add.completeCollectionToCart');
    const handleCreateNewListClicked = onCreateNewListClicked ? onCreateNewListClicked : () => {};

    return (
        <div className="selection-detail-list bundle-header">
            <span className="title">{bundle?.title}</span>
            <AnalyseDropDown
                analysis={analyse}
                tableComponent={AnalyseTable}
                onAnalyseClicked={onAnalyseClicked}
                label={t('bundles.analyseCollection')}
                showDropdown={isAnalyseOpen}
                isLoading={isAnalyseLoading}
            />
            <div style={{ flexGrow: 1 }} />
            <div className="action-buttons-container">
                <TotalAmountBadges {...totals} />
                {bundle?.price || bundle?.basePrice ? (
                    <div className="discount-line">
                        <BundleDiscountLine bundle={bundle} />
                    </div>
                ) : null}
            </div>

            <BulkActionButtons
                listLabel={listLabel}
                cartLabel={cartLabel}
                selectionLists={selectionLists}
                onCreateNewListClicked={handleCreateNewListClicked}
                onBulkAddToList={handleBulkAddToList}
                onBulkAddToCart={handleBulkAddToCart}
                value={priceTier}
                onChange={setPriceTier}
                onlyEpressProducts={bundle?.loanFormat ? mapLoanFormat(bundle.loanFormat) === LoanFormat.ePress : false}
                hasSingleUsageCondition={hasSingleUsageCondition}
            />
        </div>
    );
};
